import { defineEvent } from "@visioni/a3s-analytics";
import type { EditUserFormData } from "@visioni/a3s-access/app";
import type { RequestErrorType } from "@src/services/api/error-type";

type TenantScope = { tenant_id: string | undefined };

/** The user's access after the save, as counts. */
export type AccessGrantProps = {
  tenant_admin: boolean;
  site_manager_count: number;
  site_viewer_count: number;
  camera_manager_count: number;
  camera_viewer_count: number;
};

type AccessGrant = Pick<EditUserFormData, "tenantAdmin" | "sites" | "cameras">;

function countRole(grants: ReadonlyMap<string, "manager" | "viewer">, role: "manager" | "viewer"): number {
  return [...grants.values()].filter((r) => r === role).length;
}

export function accessGrantProps(grant: AccessGrant): AccessGrantProps {
  return {
    tenant_admin: grant.tenantAdmin,
    site_manager_count: countRole(grant.sites, "manager"),
    site_viewer_count: countRole(grant.sites, "viewer"),
    camera_manager_count: countRole(grant.cameras, "manager"),
    camera_viewer_count: countRole(grant.cameras, "viewer"),
  };
}

/** A save with no access left removes the user from the tenant. */
export function grantsNoAccess(grant: AccessGrant): boolean {
  return !grant.tenantAdmin && grant.sites.size === 0 && grant.cameras.size === 0;
}

export function isNoAccountError(error: unknown): boolean {
  return error instanceof Error && error.message.includes("does not have an existing account");
}

export type InviteErrorType = RequestErrorType | "no_account";

export const UserInvited = defineEvent<TenantScope & AccessGrantProps>("User Invited");
export const UserInviteFailed = defineEvent<TenantScope & AccessGrantProps & { error_type: InviteErrorType }>(
  "User Invite Failed",
);

export const UserPermissionsSaved = defineEvent<TenantScope & AccessGrantProps>("User Permissions Saved");
export const UserPermissionsFailed = defineEvent<TenantScope & AccessGrantProps & { error_type: RequestErrorType }>(
  "User Permissions Failed",
);

type RemovalMethod = { method: "remove_all" | "cleared_permissions" };
export const UserAccessRemoved = defineEvent<TenantScope & RemovalMethod>("User Access Removed");
export const UserAccessRemovalFailed = defineEvent<TenantScope & RemovalMethod & { error_type: RequestErrorType }>(
  "User Access Removal Failed",
);
